import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Calendar,
    Clock,
    Video,
    Sparkles,
    MessageCircle,
    Mail,
    ArrowRight,
    X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE, API } from "@/lib/site";

const FIXED_TIMES = ["10:00", "14:30", "16:00"];

// Generate next 10 business days (Mon-Fri), 3 fixed slots per day.
const buildSlotPreview = (lang) => {
    const dayLabels =
        lang === "es"
            ? ["LU", "MA", "MI", "JU", "VI"]
            : ["MO", "DI", "MI", "DO", "FR"];
    const locale = lang === "es" ? "es-ES" : "de-CH";
    const slots = [];
    let cursor = new Date();
    let added = 0;
    while (added < 10) {
        cursor.setDate(cursor.getDate() + 1);
        const dow = cursor.getDay();
        if (dow === 0 || dow === 6) continue; // skip weekends
        slots.push({
            day: dayLabels[(dow + 6) % 7] || dayLabels[0],
            date: String(cursor.getDate()).padStart(2, "0"),
            month: cursor.toLocaleString(locale, { month: "short" }),
            iso: cursor.toISOString().slice(0, 10),
            fullDate: new Date(cursor),
            times: FIXED_TIMES,
        });
        added++;
    }
    return slots;
};

const Booking = () => {
    const { t, lang } = useLanguage();
    const navigate = useNavigate();
    const bookingEnabled = SITE.bookingEmbed.enabled && SITE.bookingEmbed.url;
    const bookingUrl = bookingEnabled ? SITE.bookingEmbed.url : null;

    const slots = buildSlotPreview(lang);

    // Native booking modal state
    const [selected, setSelected] = useState(null); // {isoDate, time, label}
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [sending, setSending] = useState(false);

    const openSlot = (slot, time) => {
        const locale = lang === "es" ? "es-ES" : "de-CH";
        const label = `${slot.fullDate.toLocaleDateString(locale, {
            weekday: "long",
            day: "2-digit",
            month: "long",
        })} · ${time}`;
        setSelected({ iso: `${slot.iso}T${time}`, time, label });
    };

    const closeModal = () => {
        if (sending) return;
        setSelected(null);
        setForm({ name: "", email: "", phone: "" });
    };

    const submitBooking = async (e) => {
        e.preventDefault();
        if (!selected || !form.name || !form.email || !form.phone) return;
        setSending(true);
        try {
            await axios.post(`${API}/booking-requests`, {
                name: form.name,
                email: form.email,
                phone: form.phone,
                slot_iso: selected.iso,
                slot_label: selected.label,
                language: lang,
            });
            toast.success(t.booking.modal.success);
            setSelected(null);
            setForm({ name: "", email: "", phone: "" });
        } catch {
            toast.error(t.booking.modal.error);
        } finally {
            setSending(false);
        }
    };

    const goToAnalysis = () => navigate("/analyse");

    return (
        <section
            id="booking"
            data-testid="booking-section"
            className="py-24 md:py-32 bg-ella-cream relative overflow-hidden"
        >
            {/* Soft brand glow */}
            <div className="absolute -top-40 left-1/3 w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle_at_center,rgba(193,154,107,0.12),transparent_60%)] pointer-events-none" />
            <div className="absolute -bottom-40 right-0 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle_at_center,rgba(183,110,121,0.10),transparent_60%)] pointer-events-none" />

            <div className="ella-container grid lg:grid-cols-12 gap-12 items-start relative">
                {/* Left: heading + meta facts */}
                <div className="lg:col-span-5">
                    <span className="ella-overline" data-testid="booking-overline">
                        {t.booking.overline}
                    </span>
                    <h2
                        className="ella-headline text-4xl md:text-5xl lg:text-6xl mt-4 text-ella-ink"
                        data-testid="booking-title"
                    >
                        {t.booking.title}
                    </h2>
                    <p className="mt-6 text-lg text-ella-ink/70 font-light">
                        {t.booking.subtitle}
                    </p>

                    {/* Meta facts */}
                    <ul
                        className="mt-10 space-y-5"
                        data-testid="booking-meta-list"
                    >
                        <li className="flex items-start gap-4">
                            <span className="w-10 h-10 rounded-full bg-ella-blush/50 flex items-center justify-center text-ella-rose-dark flex-shrink-0">
                                <Clock className="w-4 h-4" strokeWidth={1.5} />
                            </span>
                            <div>
                                <p className="ella-overline">{t.booking.duration_label}</p>
                                <p className="font-serif text-xl text-ella-ink mt-0.5">
                                    {t.booking.duration}
                                </p>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="w-10 h-10 rounded-full bg-ella-blush/50 flex items-center justify-center text-ella-rose-dark flex-shrink-0">
                                <Video className="w-4 h-4" strokeWidth={1.5} />
                            </span>
                            <div>
                                <p className="ella-overline">{t.booking.type_label}</p>
                                <p className="font-serif text-xl text-ella-ink mt-0.5">
                                    {t.booking.type}
                                </p>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="w-10 h-10 rounded-full bg-ella-blush/50 flex items-center justify-center text-ella-rose-dark flex-shrink-0">
                                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                            </span>
                            <div>
                                <p className="ella-overline">{t.booking.cost_label}</p>
                                <p className="font-serif text-xl text-ella-ink mt-0.5">
                                    {t.booking.cost}
                                </p>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="w-10 h-10 rounded-full bg-ella-blush/50 flex items-center justify-center text-ella-rose-dark flex-shrink-0">
                                <Calendar className="w-4 h-4" strokeWidth={1.5} />
                            </span>
                            <div>
                                <p className="ella-overline">
                                    {t.booking.availability_label}
                                </p>
                                <p className="font-serif text-xl text-ella-ink mt-0.5">
                                    {t.booking.availability}
                                </p>
                            </div>
                        </li>
                    </ul>
                </div>

                {/* Right: premium booking frame */}
                <div className="lg:col-span-7" data-testid="booking-embed-wrapper">
                    {/* Premium gold frame wrapper that always wraps the calendar */}
                    <div className="relative">
                        {/* Outer gold gradient frame */}
                        <div className="absolute -inset-1 rounded-[2.25rem] bg-gradient-to-br from-ella-gold/40 via-ella-blush/40 to-ella-gold/30 blur-md opacity-70" />

                        <div className="relative rounded-[2rem] overflow-hidden border border-ella-gold/40 bg-ella-cream shadow-[0_24px_70px_rgba(44,42,41,0.10)]">
                            {/* Frame header bar — feels like a card masthead */}
                            <div className="flex items-center justify-between gap-4 px-6 md:px-8 py-5 border-b border-ella-line bg-white/70 backdrop-blur">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ella-blush to-ella-gold/30 flex items-center justify-center">
                                        <Calendar
                                            className="w-4 h-4 text-ella-rose-dark"
                                            strokeWidth={1.5}
                                        />
                                    </div>
                                    <div>
                                        <p className="font-serif text-lg leading-none text-ella-ink">
                                            {t.booking.preview_title}
                                        </p>
                                        <p className="ella-overline mt-1.5">
                                            {SITE.coachName}
                                        </p>
                                    </div>
                                </div>
                                {!bookingEnabled && (
                                    <span
                                        className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-ella-gold/15 text-ella-gold-dark text-[10px] uppercase tracking-[0.22em]"
                                        data-testid="booking-soon-badge"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-ella-gold animate-pulse" />
                                        {t.booking.soon_badge}
                                    </span>
                                )}
                            </div>

                            {bookingEnabled ? (
                                /* LoopBook iframe — wrapped in branded frame so it feels native */
                                <>
                                    <div className="relative bg-ella-cream">
                                        <iframe
                                            src={bookingUrl}
                                            title="ELLA Online Coaching · Online buchen"
                                            className="w-full h-[680px] sm:h-[760px] lg:h-[820px] block bg-ella-cream border-0"
                                            data-testid="booking-iframe"
                                            loading="lazy"
                                            allow="payment *; clipboard-write"
                                        />
                                    </div>

                                    {/* WhatsApp safety net — visible below the booking embed even on mobile,
                                        so a guest who doesn't see / can't load the iframe can still reach Mercedes instantly. */}
                                    <div
                                        className="px-6 md:px-10 py-7 border-t border-ella-line bg-gradient-to-b from-ella-cream/0 to-ella-sand/40"
                                        data-testid="booking-fallback-strip"
                                    >
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                            <div>
                                                <p className="ella-overline">
                                                    {t.booking.fallback_label}
                                                </p>
                                                <p className="font-serif text-base text-ella-ink/80 mt-1 leading-snug max-w-sm">
                                                    {t.booking.fallback_lead}
                                                </p>
                                            </div>
                                            <div className="flex flex-wrap gap-3">
                                                <a
                                                    href={SITE.contact.whatsappUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid="booking-cta-whatsapp"
                                                    className="btn-primary"
                                                >
                                                    <MessageCircle
                                                        className="w-4 h-4"
                                                        strokeWidth={1.5}
                                                    />
                                                    {t.booking.cta_whatsapp}
                                                </a>
                                                <a
                                                    href={`mailto:${SITE.contact.email}?subject=Erstgespr%C3%A4ch%20-%20ELLA%20Online%20Coaching`}
                                                    data-testid="booking-cta-email"
                                                    className="btn-secondary"
                                                >
                                                    <Mail
                                                        className="w-4 h-4"
                                                        strokeWidth={1.5}
                                                    />
                                                    {t.booking.cta_email}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                /* Native branded booking calendar */
                                <div
                                    className="px-6 md:px-10 py-10 bg-gradient-to-b from-white via-ella-cream to-ella-sand"
                                    data-testid="booking-fallback"
                                >
                                    <p className="font-serif italic text-2xl md:text-3xl text-ella-ink leading-snug max-w-md">
                                        {t.booking.preview_lead}
                                    </p>

                                    <p className="ella-overline mt-7">
                                        {t.booking.times_overline}
                                    </p>

                                    {/* Slot grid: 10 weekdays × 3 fixed times, all clickable */}
                                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
                                        {slots.map((s, i) => (
                                            <div
                                                key={i}
                                                className="rounded-2xl border border-ella-line bg-white/80 backdrop-blur p-3 md:p-4 text-center"
                                                data-testid={`booking-slot-${i}`}
                                            >
                                                <p className="text-[10px] uppercase tracking-[0.22em] text-ella-muted">
                                                    {s.day}
                                                </p>
                                                <p className="font-serif text-2xl md:text-3xl text-ella-ink leading-none mt-1.5">
                                                    {s.date}
                                                </p>
                                                <p className="text-[10px] uppercase tracking-[0.18em] text-ella-gold-dark mt-1">
                                                    {s.month}
                                                </p>
                                                <div className="mt-3 space-y-1.5">
                                                    {s.times.map((tm) => (
                                                        <button
                                                            key={tm}
                                                            type="button"
                                                            onClick={() => openSlot(s, tm)}
                                                            data-testid={`booking-slot-${i}-time-${tm.replace(":", "")}`}
                                                            className="block w-full text-[11px] md:text-xs rounded-full border border-ella-line bg-white hover:border-ella-gold hover:bg-ella-gold hover:text-white text-ella-ink py-1 transition-colors"
                                                        >
                                                            {tm}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <p
                                        className="mt-8 text-sm text-ella-muted italic leading-relaxed"
                                        data-testid="booking-placeholder-note"
                                    >
                                        {t.booking.fallback_lead}
                                    </p>

                                    {/* CTA cluster — premium fallback contact options */}
                                    <div className="mt-7 flex flex-wrap gap-3">
                                        <a
                                            href={SITE.contact.whatsappUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid="booking-cta-whatsapp"
                                            className="btn-primary"
                                        >
                                            <MessageCircle
                                                className="w-4 h-4"
                                                strokeWidth={1.5}
                                            />
                                            {t.booking.cta_whatsapp}
                                        </a>
                                        <a
                                            href={`mailto:${SITE.contact.email}?subject=Erstgespr%C3%A4ch%20-%20ELLA%20Online%20Coaching`}
                                            data-testid="booking-cta-email"
                                            className="btn-secondary"
                                        >
                                            <Mail
                                                className="w-4 h-4"
                                                strokeWidth={1.5}
                                            />
                                            {t.booking.cta_email}
                                        </a>
                                        <button
                                            onClick={goToAnalysis}
                                            data-testid="booking-cta-apply"
                                            className="btn-ghost"
                                        >
                                            {t.booking.cta_apply}
                                            <ArrowRight
                                                className="w-4 h-4"
                                                strokeWidth={1.5}
                                            />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Native booking modal — opens when a slot is clicked */}
            {selected && (
                <div
                    className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-ella-ink/50 backdrop-blur-sm animate-in fade-in duration-200"
                    data-testid="booking-modal"
                    onClick={closeModal}
                >
                    <div
                        className="relative w-full max-w-md rounded-[2rem] bg-ella-cream border border-ella-gold/30 shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={closeModal}
                            disabled={sending}
                            data-testid="booking-modal-close"
                            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/70 border border-ella-line hover:bg-white flex items-center justify-center text-ella-ink transition-colors disabled:opacity-40"
                            aria-label={t.booking.modal.cancel}
                        >
                            <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>

                        <div className="p-7 md:p-9">
                            <p className="ella-overline">{t.booking.overline}</p>
                            <h3
                                className="font-serif text-2xl md:text-3xl mt-3 text-ella-ink leading-tight"
                                data-testid="booking-modal-title"
                            >
                                {t.booking.modal.title}
                            </h3>
                            <p className="mt-3 text-sm text-ella-ink/70 leading-relaxed">
                                {t.booking.modal.subtitle}
                            </p>

                            <div className="mt-5 rounded-2xl border border-ella-gold/30 bg-white/70 px-4 py-3">
                                <p className="text-[10px] uppercase tracking-[0.22em] text-ella-gold-dark">
                                    {t.booking.modal.slot_label}
                                </p>
                                <p
                                    className="font-serif text-lg text-ella-ink mt-1"
                                    data-testid="booking-modal-slot"
                                >
                                    {selected.label}
                                </p>
                            </div>

                            <form
                                onSubmit={submitBooking}
                                className="mt-6 space-y-3"
                                data-testid="booking-modal-form"
                            >
                                <input
                                    type="text"
                                    required
                                    minLength={2}
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm({ ...form, name: e.target.value })
                                    }
                                    placeholder={t.booking.modal.name}
                                    data-testid="booking-modal-name"
                                    className="w-full px-4 py-3 rounded-xl border border-ella-line bg-white text-sm text-ella-ink placeholder:text-ella-muted outline-none focus:border-ella-gold transition-colors"
                                />
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) =>
                                        setForm({ ...form, email: e.target.value })
                                    }
                                    placeholder={t.booking.modal.email}
                                    data-testid="booking-modal-email"
                                    className="w-full px-4 py-3 rounded-xl border border-ella-line bg-white text-sm text-ella-ink placeholder:text-ella-muted outline-none focus:border-ella-gold transition-colors"
                                />
                                <input
                                    type="tel"
                                    required
                                    minLength={5}
                                    value={form.phone}
                                    onChange={(e) =>
                                        setForm({ ...form, phone: e.target.value })
                                    }
                                    placeholder={t.booking.modal.phone}
                                    data-testid="booking-modal-phone"
                                    className="w-full px-4 py-3 rounded-xl border border-ella-line bg-white text-sm text-ella-ink placeholder:text-ella-muted outline-none focus:border-ella-gold transition-colors"
                                />

                                <button
                                    type="submit"
                                    disabled={sending}
                                    data-testid="booking-modal-submit"
                                    className="btn-primary w-full justify-center mt-5 disabled:opacity-60"
                                >
                                    {sending
                                        ? t.booking.modal.submitting
                                        : t.booking.modal.submit}
                                    {!sending && (
                                        <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                                    )}
                                </button>

                                <p className="text-[11px] text-ella-muted text-center mt-3 leading-relaxed">
                                    {t.booking.modal.privacy}
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Booking;
